/**
 * Pack CRUD operations — create, update, delete, query packs.
 *
 * Packs are reusable plan templates stored in the `plan` table
 * with kind="pack". They define steps, questions, and automations
 * that executePlan() uses to generate tasks.
 */

import { getDb } from "./runtime.js"
import { ok, fail } from "./op-types.js"
import type { OpResult } from "./op-types.js"
import type { DbPack } from "./planTypes.js"

// ---------------------------------------------------------------------------
// addPack
// ---------------------------------------------------------------------------

export interface AddPackOpts {
  name: string
  category: string
  anchorDateType: DbPack["anchorDateType"]
  steps: DbPack["steps"]
  description?: string
  icon?: string
  triggers?: string[]
  questions?: DbPack["questions"]
  automations?: DbPack["automations"]
  locale?: string
  isBuiltIn?: boolean
  isPublished?: boolean
}

/**
 * Create a new pack template.
 */
export async function addPack(
  opts: AddPackOpts,
): Promise<OpResult<{ id: string }>> {
  const db = getDb()
  try {
    const id = crypto.randomUUID()
    const pack: DbPack = {
      id,
      kind: "pack",
      name: opts.name,
      description: opts.description,
      icon: opts.icon,
      category: opts.category,
      triggers: opts.triggers,
      anchorDateType: opts.anchorDateType,
      questions: opts.questions,
      steps: opts.steps,
      automations: opts.automations,
      locale: opts.locale,
      version: 1,
      isBuiltIn: opts.isBuiltIn,
      isPublished: opts.isPublished,
      creatorId: db.cloud.currentUserId,
      owner: db.cloud.currentUserId,
    }
    await db.plan.add(pack)
    return ok({ id })
  } catch (e) {
    return fail(`addPack failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// updatePack
// ---------------------------------------------------------------------------

export type UpdatePackOpts = Partial<
  Pick<
    DbPack,
    | "name"
    | "description"
    | "icon"
    | "category"
    | "triggers"
    | "anchorDateType"
    | "questions"
    | "steps"
    | "automations"
    | "locale"
    | "isPublished"
  >
>

/**
 * Update a pack template. Bumps the version automatically.
 */
export async function updatePack(
  id: string,
  updates: UpdatePackOpts,
): Promise<OpResult> {
  const db = getDb()
  try {
    const existing = await db.plan.get(id)
    if (!existing || existing.kind !== "pack")
      return fail("Pack not found")

    const pack = existing as DbPack
    await (db.plan as import("dexie").Table<DbPack, string>).update(id, {
      ...updates,
      version: pack.version + 1,
    })
    return ok()
  } catch (e) {
    return fail(`updatePack failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// deletePack
// ---------------------------------------------------------------------------

/**
 * Permanently delete a pack template.
 */
export async function deletePack(id: string): Promise<OpResult> {
  const db = getDb()
  try {
    const existing = await db.plan.get(id)
    if (!existing || existing.kind !== "pack")
      return fail("Pack not found")

    await db.plan.delete(id)
    return ok()
  } catch (e) {
    return fail(`deletePack failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// getPack
// ---------------------------------------------------------------------------

/**
 * Get a single pack by ID.
 */
export async function getPack(id: string): Promise<DbPack | undefined> {
  const db = getDb()
  const entry = await db.plan.get(id)
  if (entry?.kind === "pack") return entry as DbPack
  return undefined
}

// ---------------------------------------------------------------------------
// listPacks
// ---------------------------------------------------------------------------

export interface ListPacksOpts {
  category?: string
  builtInOnly?: boolean
}

/**
 * List available pack templates, optionally filtered.
 */
export async function listPacks(opts?: ListPacksOpts): Promise<DbPack[]> {
  const db = getDb()
  let entries: import("./planTypes.js").DbPlanEntry[]

  if (opts?.category) {
    entries = await db.plan
      .where("category")
      .equals(opts.category)
      .filter((e) => e.kind === "pack")
      .toArray()
  } else {
    entries = await db.plan
      .where("kind")
      .equals("pack")
      .toArray()
  }

  let packs = entries.filter((e) => e.kind === "pack") as DbPack[]

  if (opts?.builtInOnly) {
    packs = packs.filter((p) => p.isBuiltIn)
  }

  return packs
}
